import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Sliders, Save } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { useAuthStore } from '@/lib/auth-store'

interface ArtistTogglesPayload {
  artistName: string
  /** Artist's stored override map — explicit overrides only. */
  stored: Record<string, boolean>
  /** Org's stored map (the default this artist inherits from). */
  orgDefault: Record<string, boolean>
  /** Resolved per-feature state (override > org > plan default). */
  resolved: Record<string, boolean>
  available: readonly string[]
}

const SECTION_GROUPS: Array<{ label: string; keys: string[] }> = [
  {
    label: 'Performance flow',
    keys: [
      'pipeline', 'shows', 'ticketing', 'merch', 'travel',
      'logistics', 'territory', 'broker', 'contracts',
      'setlists', 'debriefs', 'wellness', 'cancellations', 'vip',
    ],
  },
  {
    label: 'Artist-level',
    keys: ['press', 'marketing', 'analytics', 'royalties', 'team'],
  },
  {
    label: 'Tooling',
    keys: ['crew_pool', 'team_permissions', 'email_rules', 'assistant'],
  },
]

const FEATURE_LABELS: Record<string, string> = {
  pipeline: 'Pipeline & inbox',
  shows: 'Shows',
  ticketing: 'Ticketing',
  merch: 'Merch',
  travel: 'Travel',
  logistics: 'Logistics',
  territory: 'Territory routing',
  broker: 'Brokers',
  contracts: 'Contracts',
  setlists: 'Setlists',
  debriefs: 'Debriefs',
  wellness: 'Wellness Guardian',
  cancellations: 'Cancellations',
  vip: 'VIP Experiences',
  press: 'Press & Media',
  marketing: 'Marketing & Advertising',
  analytics: 'Analytics',
  royalties: 'Royalties',
  team: 'Team',
  crew_pool: 'Crew talent pool',
  team_permissions: 'Team permissions',
  email_rules: 'Email rules',
  assistant: 'AI Assistant',
}

type TriState = 'inherit' | 'on' | 'off'

function currentTriState(
  stored: Record<string, boolean>,
  pending: Record<string, boolean | null>,
  key: string,
): TriState {
  if (key in pending) {
    const v = pending[key]
    if (v === null) return 'inherit'
    return v ? 'on' : 'off'
  }
  if (Object.prototype.hasOwnProperty.call(stored, key)) {
    return stored[key] ? 'on' : 'off'
  }
  return 'inherit'
}

export function ArtistSettingsPage() {
  const user = useAuthStore((s) => s.user)
  const qc = useQueryClient()
  const { data, error, isLoading } = useQuery<ArtistTogglesPayload, ApiClientError>({
    queryKey: ['artist-modules', user?.artistId],
    queryFn: () => artistFetch<ArtistTogglesPayload>('/modules'),
    enabled: !!user?.artistId,
  })

  const [pending, setPending] = useState<Record<string, boolean | null>>({})
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)

  useEffect(() => {
    setPending({})
  }, [data?.stored])

  const canEdit = user?.role === 'admin'

  function setKey(key: string, state: TriState) {
    setPending((prev) => ({
      ...prev,
      [key]: state === 'inherit' ? null : state === 'on',
    }))
  }

  async function save() {
    if (Object.keys(pending).length === 0) return
    setSaving(true)
    setSaveErr(null)
    try {
      await artistFetch('/modules', {
        method: 'PATCH',
        body: { patch: pending },
      })
      await qc.invalidateQueries({ queryKey: ['artist-modules', user?.artistId] })
      await qc.invalidateQueries({ queryKey: ['nav-artist-modules'] })
      setPending({})
    } catch (err) {
      setSaveErr(err instanceof ApiClientError ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const pendingCount = useMemo(() => Object.keys(pending).length, [pending])

  return (
    <div>
      <PageHeader
        title="Artist Settings & Modules"
        subtitle={
          data
            ? `${data.artistName} • Per-artist module overrides (inherit, force on, force off)`
            : 'Override org module defaults for this artist.'
        }
        actions={
          pendingCount > 0 && (
            <button
              type="button"
              onClick={save}
              disabled={saving || !canEdit}
              className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving…' : `Save ${pendingCount} change${pendingCount === 1 ? '' : 's'}`}
            </button>
          )
        }
      />
      {saveErr && (
        <div className="mb-4 rounded-md bg-red-50 px-4 py-2 text-sm text-red-700">{saveErr}</div>
      )}
      {!canEdit && (
        <div className="mb-4 rounded-md bg-amber-50 px-4 py-2 text-sm text-amber-800">
          You can view module overrides but only artist admins can change them.
        </div>
      )}
      <AsyncBoundary isLoading={isLoading} error={error}>
        {!user?.artistId ? (
          <div className="rounded-md border border-dashed border-ovation-ink/15 bg-ovation-paper/40 px-4 py-8 text-center text-sm text-ovation-muted">
            <p>Select an artist from the switcher above to manage module overrides.</p>
          </div>
        ) : data ? (
          <div className="space-y-6">
            {SECTION_GROUPS.map((group) => (
              <section key={group.label} className="rounded-lg border border-ovation-ink/10 bg-white p-5">
                <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-ovation-ink">
                  <Sliders className="h-4 w-4" />
                  {group.label}
                </h2>
                <p className="mb-4 text-xs text-ovation-muted">
                  <strong>Inherit</strong> follows the organisation default. <strong>On</strong> / <strong>Off</strong> override it for this artist only.
                </p>
                <ul className="divide-y divide-ovation-ink/5">
                  {group.keys
                    .filter((k) => (data.available as readonly string[]).includes(k))
                    .map((key) => {
                      const tri = currentTriState(data.stored, pending, key)
                      const orgHas = Object.prototype.hasOwnProperty.call(data.orgDefault, key)
                      const orgVal = data.orgDefault[key]
                      const effective = data.resolved[key]
                      return (
                        <li
                          key={key}
                          className="flex flex-wrap items-center justify-between gap-3 py-3"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-ovation-ink">
                              {FEATURE_LABELS[key] ?? key}
                            </div>
                            <div className="text-[11px] uppercase tracking-wider text-ovation-muted">
                              Currently {effective ? 'enabled' : 'disabled'}
                              {tri === 'inherit' && (
                                <> · inherits {orgHas ? `org (${orgVal ? 'on' : 'off'})` : 'plan default'}</>
                              )}
                            </div>
                          </div>
                          <TriStatePicker
                            value={tri}
                            onChange={(v) => setKey(key, v)}
                            disabled={!canEdit}
                          />
                        </li>
                      )
                    })}
                </ul>
              </section>
            ))}
          </div>
        ) : !isLoading && !error ? (
          <div className="rounded-md border border-dashed border-ovation-ink/15 bg-ovation-paper/40 px-4 py-8 text-center text-sm text-ovation-muted">
            <p>Module settings could not be loaded for this artist.</p>
          </div>
        ) : null}
      </AsyncBoundary>
    </div>
  )
}

function TriStatePicker({
  value,
  onChange,
  disabled,
}: {
  value: TriState
  onChange: (v: TriState) => void
  disabled?: boolean
}) {
  const states: Array<{ id: TriState; label: string }> = [
    { id: 'inherit', label: 'Inherit' },
    { id: 'on', label: 'On' },
    { id: 'off', label: 'Off' },
  ]
  return (
    <div className="flex items-center rounded-md border border-ovation-ink/15 bg-white overflow-hidden">
      {states.map((s, i) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onChange(s.id)}
          disabled={disabled}
          className={`px-3 py-1 text-xs font-medium transition-colors ${
            value === s.id
              ? 'bg-ovation-ink text-ovation-paper'
              : 'text-ovation-ink hover:bg-ovation-paper'
          } ${i > 0 ? 'border-l border-ovation-ink/10' : ''} disabled:opacity-50`}
        >
          {s.label}
        </button>
      ))}
    </div>
  )
}
